import React from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, BookOpen, Building2, Camera, Clock, Database, Info, Lightbulb, Phone, Star, Utensils } from "lucide-react";
import { TspBreadcrumb, TspEmptyState, TspSkeleton } from "../../components/primitives";
import TspLoadError from "../../components/common/TspLoadError";
import TspSectionNav from "../../components/patterns/TspSectionNav";
import useTspScrollSpy from "../../hooks/useTspScrollSpy";
import { formatDate } from "../../utils/tspFormat";
import { toPositiveInt } from "../../utils/tspIds";
import { useTspCityKnowledge, useTspKnowledgePerms } from "./useTspKnowledgeData";
import { CityKnowledgeSections } from "./components/CitySections";

const SECTIONS = [
  { id: "overview", label: "Overview", icon: Info },
  { id: "visit-history", label: "Visit History", icon: Clock },
  { id: "contacts", label: "Contacts", icon: Phone },
  { id: "accommodation", label: "Accommodation", icon: Building2 },
  { id: "prasadam", label: "Prasadam", icon: Utensils },
  { id: "book-distribution", label: "Book Distribution", icon: BookOpen },
  { id: "public-response", label: "Public Response", icon: Star },
  { id: "media", label: "Media", icon: Camera },
  { id: "team-advice", label: "Team Advice", icon: Lightbulb },
];
const SECTION_IDS = SECTIONS.map((section) => section.id);

// Dashboard > (the journey it was opened from) > ...
const baseCrumbs = (fromJourneyId) => [{ label: "Dashboard", to: "/tsp" }, ...(fromJourneyId ? [{ label: "Journey", to: `/tsp/journeys/${fromJourneyId}` }] : [])];

// "Back": to the journey this page was opened from ("View Summary" links carry
// ?journey=<id>, so this survives a refresh). Opened from anywhere else (media,
// advice, search), back to wherever the person came from, or the TSP dashboard.
function useGoBack(fromJourneyId) {
  const navigate = useNavigate();
  const { key } = useLocation();
  return () => {
    if (fromJourneyId) navigate(`/tsp/journeys/${fromJourneyId}`);
    else if (key !== "default") navigate(-1);
    else navigate("/tsp");
  };
}

function BackButton({ onClick }) {
  return (
    <button type="button" className="tsp-btn tsp-btn-outline-primary" onClick={onClick}>
      <ArrowLeft size={14} aria-hidden="true" /> Back
    </button>
  );
}

// Rendered only once the city has loaded, so the scroll-spy can find its sections.
function CityKnowledge({ city, perms, crumbs, onBack }) {
  const [activeId, setActiveId] = useTspScrollSpy(SECTION_IDS);

  return (
    <div className="tsp-page tsp-knowledge-page">
      <div className="tsp-page-heading-row">
        <div>
          <h1>{city.location.label}</h1>
          <TspBreadcrumb items={[...crumbs, city.location.city_name]} />
        </div>
        <div className="tsp-knowledge-heading-aside">
          <span className="tsp-muted-text tsp-knowledge-updated">Last visited {formatDate(String(city.stats.last_visited).slice(0, 10))}</span>
          <BackButton onClick={onBack} />
        </div>
      </div>

      <div className="tsp-journey-detail-layout">
        <div className="tsp-journey-detail-main">
          <CityKnowledgeSections city={city} perms={perms} />
        </div>

        <TspSectionNav sections={SECTIONS} activeId={activeId} onSelect={setActiveId}>
          {perms.viewLocations && (
            <div className="tsp-card">
              <h4 className="tsp-section-nav-title">Related</h4>
              <div className="tsp-quick-actions-list">
                <Link to="/tsp/locations" className="tsp-btn tsp-btn-outline-info tsp-quick-action-full">
                  Location Repository
                </Link>
              </div>
            </div>
          )}
        </TspSectionNav>
      </div>
    </div>
  );
}

export default function TspKnowledgeCityPage() {
  const { cityId } = useParams();
  const locationId = toPositiveInt(cityId); // the id of the city's location; anything else is not a city
  // Opened from a journey's "View Summary" (?journey=<id>): "Back" returns to that journey.
  const fromJourneyId = toPositiveInt(new URLSearchParams(useLocation().search).get("journey"));
  const goBack = useGoBack(fromJourneyId);
  const crumbs = baseCrumbs(fromJourneyId);
  const { data: city, loading, error, reload } = useTspCityKnowledge(locationId);
  const perms = useTspKnowledgePerms();

  const notFound = !locationId || error?.response?.status === 404;

  if (loading && locationId) {
    return (
      <div className="tsp-page tsp-knowledge-page">
        <TspBreadcrumb items={[...crumbs, "City"]} />
        <TspSkeleton variant="row" count={4} label="Loading city knowledge" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="tsp-page tsp-knowledge-page">
        <TspBreadcrumb items={[...crumbs, "Not found"]} />
        <TspEmptyState
          icon={Database}
          title="No knowledge recorded for this city yet"
          message="A city's summary is available once a journey that visited it has been completed."
          action={<BackButton onClick={goBack} />}
        />
      </div>
    );
  }

  if (error || !city) {
    return (
      <div className="tsp-page tsp-knowledge-page">
        <TspBreadcrumb items={[...crumbs, "City"]} />
        <TspLoadError error={error} onRetry={reload} what="this city" />
      </div>
    );
  }

  return <CityKnowledge key={city.location.id} city={city} perms={perms} crumbs={crumbs} onBack={goBack} />;
}
